import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCanAccess, useNotify } from 'ra-core';
import {
  lireLeReglageDuParc,
  poserLeReglageDuParc,
} from '@groupe-trousseau/shared/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { deballer, lireLErreur } from '../../fournisseurs/client';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE RÉGLAGE DU PARC — F-65 / DD-61, ET IL LIT DÉSORMAIS PAR SA PROPRE ROUTE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * DD-61 : « **un bloc « réglage du parc » sur ce même écran, visible du compte qui voit tout
 * SEUL, fermé à la structure par refus explicite. Aucun écran nouveau n'est créé.** » Ce
 * fichier est ce bloc : il reste **une partie de l'écran des réglages**, `reglages/index.ts`
 * ne l'exporte pas, et il n'a pas d'adresse à lui.
 *
 * « **Il PLAFONNE le réglage d'élèves d'une structure sans le remplacer** […] et le réglage
 * effectif d'un cours est **le plus bas des deux**. » « **Ce n'est pas un sixième réglage de
 * structure** : c'est un **nombre du produit**, porté par personne d'autre. »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LES DEUX SOURCES ONT ÉTÉ LUES CÔTE À CÔTE AVANT CE CÂBLAGE, ET ELLES NE DISENT PAS
 * LA MÊME CHOSE — MAIS PAS DANS LE SENS QU'ON ATTEND.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Jusqu'ici ce bloc lisait le champ facultatif `parc` de `lireLesReglagesDuneStructure`, et
 * `GET /api/v1/admin/parc/reglage` n'avait **aucun appelant**. Confrontation du contrat, champ
 * par champ :
 *
 * | | champ `parc` de la réponse de structure | route `lireLeReglageDuParc` |
 * |---|---|---|
 * | `nombreMaximumElevesParCours` | oui | oui, **même valeur, même source** |
 * | `modifieLe` | oui | **NON** |
 * | adresse | `/admin/structures/:id/reglages` — **exige une structure** | `/admin/parc/reglage` — **n'en prend aucune** |
 * | refus nommé | aucun : le champ est simplement `null` | **`REGLAGE_DE_PARC_INTERDIT`** |
 *
 * Les deux services lisent la même ligne, `ReglagesService.lireLeParc()`. **La route rend donc
 * STRICTEMENT MOINS de champs**, et c'est le fait qui aurait pu conclure à un doublon au sens
 * de D-118 — « un appelant vert sur un chemin qui n'ajoute rien ».
 *
 * **CE QU'ELLE AJOUTE POURTANT, ET QUI A DÉCIDÉ : ELLE N'EXIGE PAS DE STRUCTURE.** L'écran des
 * réglages refuse de rendre quoi que ce soit tant qu'aucune structure n'est choisie — les cinq
 * réglages appartiennent à une structure, c'est juste. **Le réglage du parc, lui, n'appartient
 * à aucune**, et le bloc le dit en toutes lettres : « un nombre du produit, hors de toute
 * structure ». Il était néanmoins **inatteignable tant qu'une structure n'était pas prise dans
 * le sélecteur** — un nombre du parc gardé derrière le choix d'une structure quelconque, ce que
 * ni F-65 ni DD-61 ne demandent. C'est le seul chemin du contrat qui le lise sans structure, et
 * c'est pourquoi ce bloc se rend **hors de la garde de structure courante**.
 *
 * **ET IL FERME UN DÉFAUT QUE LA SECONDE SOURCE PORTAIT.** Le contrat déclare `parc` comme
 * `objet | null` — « **absent pour une personne qui tient un espace** ». L'écran le déclarait
 * en champ **facultatif** et testait `data.parc !== undefined` : pour un `orgadmin`, qui reçoit
 * `null`, ce test est **vrai**, le bloc se rendait, et la première lecture — `data.parc.modifieLe`
 * — déréférençait `null`. Lu au contrat, au DTO Zod (`blocDuParc.nullable()`) et au service
 * (`personne.role === Role.SUPERADMIN ? {…} : null`). En lisant par sa propre route et en se
 * gardant par la matrice, le bloc ne dépend plus d'un champ dont l'absence a deux formes.
 *
 * **CE QUE LE CHOIX COÛTE, ÉCRIT POUR QU'UN AJOUT LE CONTREDISE VISIBLEMENT.**
 * **`modifieLe` disparaît de l'écran** : `reglageDuParcResponseSchema` ne porte que le nombre.
 * Aller le chercher dans l'autre réponse ferait **deux sources pour un même objet sur un même
 * bloc**, et la seconde ne serait lisible que lorsqu'une structure est choisie — donc une date
 * qui apparaît et disparaît selon le sélecteur. **La date est retirée plutôt que fabriquée**, et
 * la question de l'ajouter au contrat de la route appartient au porteur.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE REFUS EST BRANCHÉ SUR SON NOM, ET C'EST LE PATRON DE `ChiffresDuParc`.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-65 critère 3 nomme `REGLAGE_DE_PARC_INTERDIT`, et **D-074 a posé `@CodeDeRefus` sur les
 * deux routes du parc pour que le refus porte ce nom** au lieu du générique
 * `FUNCTION_NOT_ALLOWED` — « un front qui attend le code nommé ne l'aurait jamais vu ».
 * L'écriture affichait jusqu'ici le message brut ; les deux gestes le branchent désormais de
 * la même façon.
 *
 * **Le branchement est un second verrou, pas le premier**, et il faut le savoir pour ne pas le
 * croire éprouvé : la matrice ferme la ligne `reglage-du-parc` à l'`orgadmin`, donc ce bloc
 * rend `null` chez lui **avant tout appel**. Le code nommé n'arrive donc que si la matrice et
 * la route divergent — ce qui est exactement le cas que `admin/CLAUDE.md` déclare inoffensif
 * dans les deux sens, et le seul où quelqu'un a besoin de lire ce que le serveur a refusé.
 */

/** Le type local, limité à ce que le bloc affiche. Les types du client généré ne s'importent pas. */
type ReglageLu = { nombreMaximumElevesParCours: number };

const CLEF = ['reglage-du-parc'] as const;

export function ReglageDuParc() {
  const notifier = useNotify();
  const { canAccess: peutLire, isPending: droitEnCours } = useCanAccess({
    resource: 'reglage-du-parc',
    action: 'lire-le-reglage-du-parc',
  });
  const { canAccess: peutEcrire } = useCanAccess({
    resource: 'reglage-du-parc',
    action: 'ecrire-le-reglage-du-parc',
  });
  const [enCours, setEnCours] = useState(false);

  const { data, isPending, error, refetch } = useQuery<ReglageLu, unknown>({
    queryKey: CLEF,
    queryFn: () => deballer(lireLeReglageDuParc()),
    enabled: peutLire,
    // **Un refus ne se rejoue pas trois fois.** Le défaut de la librairie est de réessayer,
    // ce qui transformerait un `403` en trois `403` — trois lignes de journal côté serveur
    // pour une règle qui ne changera pas d'avis entre deux essais.
    retry: false,
  });

  // **Le droit se lit AVANT le bloc**, jamais pendant : sans cela, le bloc du parc
  // clignoterait chez la personne qui tient un espace, à qui il est fermé.
  if (droitEnCours) return null;
  /**
   * **Rien, et non un encart de refus.** DD-61 le veut « visible du compte qui voit tout
   * SEUL », et l'écran des réglages est **le même pour les deux rôles** : servir à
   * l'`orgadmin` un encart « ce nombre ne vous regarde pas » à chaque visite lui apprendrait
   * l'existence d'un réglage sur lequel il ne peut rien. Le refus explicite que le cahier des
   * charges exige vit **sur la route**, et il porte son nom.
   */
  if (!peutLire) return null;

  const poser = async (evenement: React.FormEvent<HTMLFormElement>) => {
    evenement.preventDefault();
    const valeur = Number(new FormData(evenement.currentTarget).get('valeur'));
    if (!Number.isInteger(valeur)) {
      notifier('La valeur doit être un nombre entier.', { type: 'warning' });
      return;
    }
    setEnCours(true);
    try {
      await deballer(
        poserLeReglageDuParc({ body: { nombreMaximumElevesParCours: valeur } }),
      );
      notifier(
        'Réglage du parc enregistré. La modification laisse une ligne.',
        {
          type: 'info',
        },
      );
      await refetch();
    } catch (erreur) {
      notifier(messageDuRefus(erreur), { type: 'error' });
    } finally {
      setEnCours(false);
    }
  };

  return (
    <>
      {/* **Séparé, et non une sixième ligne.** DD-61 : « ce n'est pas un sixième réglage de
          structure : c'est un nombre du produit […] la liste fermée à cinq tient sans
          changement. » La séparation entre dans ce fichier avec le bloc : sortie, elle
          laisserait un trait seul chez la personne à qui le bloc est fermé. */}
      <Separator />
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">Réglage du parc</h3>
        <p className="text-muted-foreground text-xs">
          Un nombre du produit, hors de toute structure, et le compte qui voit
          tout est seul à le lire et à l’écrire. Il plafonne le réglage d’élèves
          d’une structure sans le remplacer : le plafond effectif d’un cours est
          le plus bas des deux. Une baisse ne touche pas un cours en train de se
          dérouler, et le réglage de la structure n’est pas modifié.
        </p>

        {isPending && <Skeleton className="h-9 w-48" />}

        {error !== null && (
          <Alert variant="destructive">
            <AlertTitle>Le réglage du parc n’a pas pu être lu.</AlertTitle>
            <AlertDescription>
              <span className="block">{messageDuRefus(error)}</span>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => void refetch()}
              >
                Réessayer
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {data !== undefined && (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => void poser(e)}
          >
            <Input
              name="valeur"
              type="number"
              defaultValue={data.nombreMaximumElevesParCours}
              className="w-32"
              disabled={!peutEcrire}
              aria-label="Nombre maximum d’élèves par cours, pour le parc"
            />
            <span className="text-muted-foreground text-sm">élèves</span>
            {peutEcrire && (
              <Button type="submit" size="sm" disabled={enCours}>
                Enregistrer
              </Button>
            )}
          </form>
        )}

        <p className="text-muted-foreground text-xs">
          À cinq, un cours ne rencontre jamais le régime dégradé de la visio —
          au-delà de six flux d’image, seuls ceux qui parlent restent en image.
          Relever cette borne le rend atteignable.
        </p>
      </section>
    </>
  );
}

/**
 * **Le refus nommé de F-65 critère 3, traduit — et rien d'autre.**
 *
 * `DOCTRINE.md` §5 : « **`code` est le contrat réel** […] jamais `message` ». Les deux routes
 * du parc ne déclarent qu'un seul code, et c'est celui-là. Tout le reste passe par le message
 * du serveur, qui **porte la borne franchie** quand il y en a une — le reformuler perdrait
 * précisément le nombre que le critère 2 fait porter.
 */
function messageDuRefus(erreur: unknown): string {
  const lue = lireLErreur(erreur);
  return lue.code === 'REGLAGE_DE_PARC_INTERDIT'
    ? 'Ce nombre est celui du parc entier, hors de toute structure : il n’est pas ouvert à ' +
        'une structure, qui garde son propre plafond d’élèves.'
    : lue.message;
}
